import { DialogComponent } from '@theme/dialog';

/**
 * Wraps all Web Storage access — it throws in some privacy modes and when
 * site data is blocked, and the popup must degrade to "just show it" rather
 * than break.
 * @param {'localStorage' | 'sessionStorage'} type
 */
function storage(type) {
  return {
    /** @param {string} key */
    get(key) {
      try {
        return window[type].getItem(key);
      } catch {
        return null;
      }
    },
    /** @param {string} key @param {string} value */
    set(key, value) {
      try {
        window[type].setItem(key, value);
      } catch {
        // Storage unavailable — popup simply may show again.
      }
    },
    /** @param {string} key */
    remove(key) {
      try {
        window[type].removeItem(key);
      } catch {
        // Storage unavailable.
      }
    },
  };
}

const local = storage('localStorage');
const session = storage('sessionStorage');

/** Retry interval while another modal (cart drawer, search…) is open. */
const BUSY_RETRY_MS = 2000;

/** Open overlays the popup must not stack on: dialogs and `<details>` drawers (mobile menu). */
const OPEN_OVERLAY_SELECTOR = 'dialog[open], details[open][scroll-lock]';

/**
 * Cookie consent (Usercentrics CMP v3, installed via the Cookiebot CMP app
 * embed). Its loader is injected at runtime and renders into a shadow root.
 */
const CONSENT_ROOT_ID = 'usercentrics-cmp-ui';
const CONSENT_DIALOG_SELECTOR = '#uc-main-dialog';
const CONSENT_LOADER_SELECTOR = 'script[src*="usercentrics"]';
const CONSENT_VIEW_EVENT = 'UC_UI_VIEW_CHANGED';

/**
 * Whether the cookie consent banner is showing or may still appear: its
 * loader is on the page but the CMP isn't initialized yet, or its banner is
 * visible. (`__ucCmp.isConsentRequired()` is not usable here — it reports
 * false while the first-visit banner is on screen.)
 * @returns {Promise<boolean>}
 */
async function isConsentPending() {
  /** @type {{ isInitialized(): Promise<boolean> } | undefined} */
  const cmp = /** @type {any} */ (window).__ucCmp;
  if (!cmp) return Boolean(document.querySelector(CONSENT_LOADER_SELECTOR));

  try {
    if (!(await cmp.isInitialized())) return true;
  } catch {
    // A broken CMP must not keep the popup away forever.
    return false;
  }

  const banner = document.getElementById(CONSENT_ROOT_ID)?.shadowRoot?.querySelector(CONSENT_DIALOG_SELECTOR);
  return banner instanceof HTMLElement && banner.offsetHeight > 0;
}

/** Default id Shopify gives every `{% form 'customer' %}`; also the redirect fragment. */
const CUSTOMER_FORM_ID = 'contact_form';

/**
 * Newsletter popup for the header group. Opens once after a configurable
 * delay, then remembers it was seen — per visitor (localStorage) when
 * "show once" is on, otherwise per browser session. A visitor who signs up
 * through the popup never sees it again.
 *
 * The email-signup block posts a regular `customer` form, which reloads the
 * page with `?customer_posted=true`. A session flag set on submit lets the
 * popup reopen on that reload so the success/error message is visible.
 *
 * In the theme editor it never auto-opens; it opens while the section (or
 * one of its blocks) is selected.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLImageElement} [image]
 *
 * @extends DialogComponent<Refs>
 */
class NewsletterPopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  /** True between `showDialog()` and its deferred `showModal()`. */
  #opening = false;

  /** True while an auto-open is postponed because something else is open. */
  #waiting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#releaseFormId();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:load', this.#handleEditorSelect);
      document.addEventListener('shopify:block:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect);
      return;
    }

    this.addEventListener('submit', this.#handleSubmit);
    window.addEventListener(CONSENT_VIEW_EVENT, this.#handleConsentView);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    this.removeEventListener('submit', this.#handleSubmit);
    window.removeEventListener(CONSENT_VIEW_EVENT, this.#handleConsentView);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:load', this.#handleEditorSelect);
    document.removeEventListener('shopify:block:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleEditorDeselect);
  }

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #storageKey() {
    return `newsletter-popup:${this.#sectionId}`;
  }

  get #seenStorage() {
    return this.dataset.showOnce === 'true' ? local : session;
  }

  #init() {
    const key = this.#storageKey;

    if (local.get(`${key}:subscribed`)) return;

    // Returning from our own form submission: reopen right away so the
    // visitor sees the result rendered by the email-signup block. Keep the
    // flag until a result is actually on the page (a captcha step can sit
    // in between).
    if (session.get(`${key}:submitted`)) {
      const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';

      if (posted || this.#hasFormMessage) {
        session.remove(`${key}:submitted`);
        if (posted) local.set(`${key}:subscribed`, 'true');
        this.#open();
        return;
      }
    }

    if (this.#seenStorage.get(`${key}:seen`)) return;

    // Count the delay from `load`: third-party overlays such as the consent
    // banner inject their loaders before it, so the busy check can see them.
    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    const schedule = () => {
      this.#timer = window.setTimeout(() => this.#openWhenIdle(), delay);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  async #openWhenIdle() {
    window.clearTimeout(this.#timer);
    this.#waiting = false;
    if (!this.isConnected) return;

    // Never stack on top of another open modal or the cookie banner (a modal
    // popup would make the banner inert); try again shortly.
    if (document.querySelector(OPEN_OVERLAY_SELECTOR) || (await isConsentPending())) {
      if (!this.isConnected) return;
      this.#waiting = true;
      this.#timer = window.setTimeout(() => this.#openWhenIdle(), BUSY_RETRY_MS);
      return;
    }

    this.#seenStorage.set(`${this.#storageKey}:seen`, 'true');
    this.#open();
  }

  /**
   * Banner closed while we wait: retry right away instead of on the next
   * tick. Banner appeared while the popup is open (rendered late, or the
   * cookie settings link): step aside so the banner stays usable, and come
   * back once it's closed.
   * @param {Event} event
   */
  #handleConsentView = (event) => {
    const { view } = /** @type {CustomEvent<{ view?: string }>} */ (event).detail ?? {};

    if (view === 'NONE') {
      if (this.#waiting) this.#openWhenIdle();
    } else if (view && this.refs.dialog.open) {
      this.closeDialog();
      this.#waiting = true;
      this.#timer = window.setTimeout(() => this.#openWhenIdle(), BUSY_RETRY_MS);
    }
  };

  get #hasFormMessage() {
    return Boolean(this.refs.dialog.querySelector('.email-signup__message'));
  }

  #open() {
    const { dialog, image } = this.refs;
    if (dialog.open || this.#opening) return;

    // The image is lazy inside a closed <dialog>; start fetching it now.
    if (image) image.loading = 'eager';

    // Unprompted opens focus the dialog itself rather than jumping into the
    // email input; after a submit, the block's own autofocus (errors) wins.
    dialog.toggleAttribute('autofocus', !this.#hasFormMessage);

    this.#labelFromHeading();
    this.#opening = true;
    this.showDialog();
    // showDialog() defers showModal() a frame; this frame is queued after it.
    requestAnimationFrame(() => {
      this.#opening = false;
    });
  }

  /**
   * Every `customer` form defaults to `id="contact_form"`, and Shopify
   * redirects submissions to `#contact_form`. Being in the header group, the
   * popup's form would come first in the DOM and swallow that fragment for
   * other signup forms (footer, articles). Rename ours and re-scroll to the
   * form that was actually submitted.
   */
  #releaseFormId() {
    const form = this.refs.dialog?.querySelector(`form#${CUSTOMER_FORM_ID}`);
    if (!form) return;

    form.id = `NewsletterPopupForm-${this.#sectionId}`;

    const submittedHere = session.get(`${this.#storageKey}:submitted`);
    if (!submittedHere && window.location.hash === `#${CUSTOMER_FORM_ID}`) {
      document.getElementById(CUSTOMER_FORM_ID)?.scrollIntoView();
    }
  }

  /** Points the dialog's accessible name at its first heading, if any. */
  #labelFromHeading() {
    const { dialog } = this.refs;
    const heading = dialog.querySelector('h1, h2, h3, h4, h5, h6');
    if (!heading) return;

    heading.id ||= `NewsletterPopupHeading-${this.#sectionId}`;
    dialog.removeAttribute('aria-label');
    dialog.setAttribute('aria-labelledby', heading.id);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    session.set(`${this.#storageKey}:submitted`, 'true');
  };

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    const detail = /** @type {CustomEvent<{ sectionId: string }>} */ (event).detail;
    if (detail?.sectionId !== this.#sectionId) return;
    this.#open();
  };

  /** @param {Event} event */
  #handleEditorDeselect = (event) => {
    const detail = /** @type {CustomEvent<{ sectionId: string }>} */ (event).detail;
    if (detail?.sectionId !== this.#sectionId) return;
    this.closeDialog();
  };
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
